import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Grid, IconButton, Modal, Tab, Tabs, Typography } from '@mui/material';

import dayjs from '../../../../config/dayjs';
import { ReportViewer, humanizeKey } from './ReportViewer';
import { exceptionReportSection, hasDeviceException } from './ExceptionReport';
import { NETWORK_REPORT_KEYS, networkReportSection } from './NetworkReport';
import { getPreviousHardware } from '../../../../services/venueAuditService';

const DATE_FORMAT = 'DD MMM YYYY, hh:mm A';
const EMPTY_VALUE = '-';

// Colours for how a value reads: a risk, worth a look, fine, or not known.
const TONES = {
    bad: { color: '#B42318', background: '#FEF3F2', border: '#FECDCA', dot: '#E6393E' },
    warn: { color: '#93370D', background: '#FFFAEB', border: '#FEDF89', dot: '#F79009' },
    good: { color: '#067647', background: '#ECFDF3', border: '#ABEFC6', dot: '#06C270' },
    unknown: { color: '#475467', background: '#F9FAFB', border: '#EAECF0', dot: '#98A2B3' },
};
const UNKNOWN_VALUES = ['Unknown', 'No report', 'Not reported', 'Not collected', 'Present (level unknown)'];
const BAD_VALUES = {
    hardwareStatus: ['FAIL'], tpm: ['None'], secureBoot: ['Disabled'], amt: ['Active'], bmc: ['Present'],
    dash: ['Detected', 'Enabled'], thunderbolt: ['Level: none'], serial: ['In use'], pci: ['Attack hardware', 'Unexpected device'],
};
const WARN_VALUES = {
    hardwareStatus: ['WARN'], tpm: ['1.2'], secureBoot: ['Not available (Legacy BIOS)'], bios: ['LEGACY'],
    amt: ['Dormant (present, not set up)', 'Disabled'], bmc: ['Declared, not confirmed'],
};
const toneOf = (key, value) => {
    if (value === undefined || value === null || UNKNOWN_VALUES.includes(value)) return 'unknown';
    if (BAD_VALUES[key]?.includes(value)) return 'bad';
    if (WARN_VALUES[key]?.includes(value)) return 'warn';
    return 'good';
};

const isEmpty = (value) => value === null || value === undefined || value === '';

const SectionTitle = ({ children, aside }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5, mt: 0.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{children}</Typography>
        {aside && <Typography variant='caption' sx={{ color: 'text.secondary' }}>{aside}</Typography>}
    </Box>
);

const ToneChip = ({ tone, label }) => (
    <Box component='span' sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: 10,
        fontSize: 12, fontWeight: 600, color: TONES[tone].color, bgcolor: TONES[tone].background, border: `1px solid ${TONES[tone].border}`,
    }}>
        <Box component='span' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TONES[tone].dot }} />
        {label}
    </Box>
);

// One hardware audit value: name, value coloured by how it reads, and the hardware behind it.
const FacetCard = ({ name, value, details, tone }) => (
    <Box sx={{
        height: '100%', p: 1.5, borderRadius: 2, border: '1px solid', borderColor: TONES[tone].border, bgcolor: TONES[tone].background,
    }}>
        <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <Box component='span' sx={{ width: 8, height: 8, flex: 'none', borderRadius: '50%', bgcolor: TONES[tone].dot }} />
            <Typography sx={{ fontWeight: 600, color: TONES[tone].color, lineHeight: 1.3 }}>{value ?? EMPTY_VALUE}</Typography>
        </Box>
        {!isEmpty(details) && <Typography variant='caption' sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.35, wordBreak: 'break-word' }}>{details}</Typography>}
    </Box>
);

const InfoItem = ({ label, value, mono }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>{label}</Typography>
        <Typography variant='body2' sx={{ fontWeight: 500, wordBreak: 'break-word', ...(mono ? { fontFamily: 'monospace', fontSize: 12.5 } : {}) }}>
            {isEmpty(value) ? EMPTY_VALUE : value}
        </Typography>
    </Box>
);

const OverviewTab = ({ device, options }) => (
    <>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 3 }}>
            <InfoItem label='Signature' value={device.deviceId} mono />
        </Box>

        <SectionTitle aside='Coloured by how each value reads for an exam machine'>Hardware audit</SectionTitle>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {options.facets.map((facet) => (
                <Grid item xs={12} sm={6} md={4} key={facet.key}>
                    <FacetCard name={facet.name} value={device.facets?.[facet.key]} details={device.details?.facets?.[facet.key]}
                        tone={toneOf(facet.key, device.facets?.[facet.key])} />
                </Grid>
            ))}
        </Grid>

        <SectionTitle>Hardware</SectionTitle>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Grid container spacing={2}>
                {(options.summary ?? []).map((part) => (
                    <Grid item xs={12} sm={6} key={part.key}>
                        <InfoItem label={part.name} value={device.details?.hardware?.[part.key]} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    </>
);

// The report as shown: without the CPU's feature flags (a hundred-odd instruction-set names), and
// without the parts the Network section shows.
const reportSections = (hardware) => {
    const shown = Object.fromEntries(Object.entries(hardware).filter(([key]) => !NETWORK_REPORT_KEYS.includes(key)));
    if (!shown.cpu || typeof shown.cpu !== 'object') return shown;
    const { flags, ...cpu } = shown.cpu;
    return { ...shown, cpu };
};

// The hardware the device's seat had before, for the exception report; asked for only when the
// device has an exception.
const usePreviousHardware = (device) => {
    const [previousHardware, setPreviousHardware] = useState({ state: 'loading', currentFingerprint: null, previous: null, change: null, basis: null });
    useEffect(() => {
        if (!hasDeviceException(device) || device.id === undefined) return undefined;
        let isCurrent = true;
        setPreviousHardware({ state: 'loading', currentFingerprint: null, previous: null, change: null, basis: null });
        getPreviousHardware(device.id)
            .then((res) => {
                if (!isCurrent) return;
                setPreviousHardware(res?.code === 'OK'
                    ? { state: 'done', currentFingerprint: res.data?.currentFingerprint ?? null, previous: res.data?.previous ?? null, change: res.data?.change ?? null, basis: res.data?.basis ?? null }
                    : { state: 'error', currentFingerprint: null, previous: null, change: null, basis: null });
            })
            .catch((error) => {
                console.log('There is an error while fetching the previous hardware', error);
                if (isCurrent) setPreviousHardware({ state: 'error', currentFingerprint: null, previous: null, change: null, basis: null });
            });
        return () => { isCurrent = false; };
    }, [device]);
    return previousHardware;
};

// The report with the device's exception and network as its first sections; the exception shows
// even when the device has sent no report.
const ReportTab = ({ device }) => {
    const hardware = useMemo(() => reportSections(device.hardware ?? {}), [device]);
    const previousHardware = usePreviousHardware(device);
    const extraSections = useMemo(() => [exceptionReportSection(device, previousHardware), networkReportSection(device)], [device, previousHardware]);
    return (
        <>
            <ReportViewer hardware={hardware} extraSections={extraSections} />
            {!Object.keys(device.hardware ?? {}).length
                && <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 6 }}>This device has not sent a hardware report.</Typography>}
        </>
    );
};

// Details of one device from the Venue Audit Dashboard. Closes on a click outside, Escape or ✕.
export const VenueDeviceDetailsModal = ({ device, options, onClose }) => {

    const [tab, setTab] = useState('overview');

    // every device opens on its overview
    useEffect(() => {
        if (device) setTab('overview');
    }, [device]);

    const hardwareStatus = device?.facets?.hardwareStatus;
    const model = device?.details?.hardware?.model;

    return (
        <Modal open={Boolean(device)} onClose={onClose} aria-labelledby='device-details-title'>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                // 85% of the screen each way; the header and tabs stay put while the body scrolls
                width: '85vw', height: '85vh', display: 'flex', flexDirection: 'column',
                bgcolor: 'background.base', borderRadius: 3, boxShadow: 24, overflow: 'hidden', outline: 'none',
            }}>
                {device && <>
                    <Box sx={{ px: 3, pt: 2.5, pb: 2, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'primary.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant='caption' sx={{ color: 'primary.main', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {device.venueName ?? 'Venue not known'}
                                </Typography>
                                <Typography id='device-details-title' variant='h5' sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                                    Seat {device.seatNo ?? EMPTY_VALUE} · {device.labNo ?? EMPTY_VALUE}
                                </Typography>
                                {!isEmpty(model) && <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.25 }}>{model}</Typography>}
                            </Box>
                            <IconButton aria-label='Close' onClick={onClose} sx={{ mt: -0.5, mr: -1, fontSize: 20, lineHeight: 1, width: 36, height: 36 }}>✕</IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                            <ToneChip tone={toneOf('hardwareStatus', hardwareStatus)} label={`Hardware ${hardwareStatus ?? 'Unknown'}`} />
                            {device.deviceStatus && <Chip size='small' variant='outlined' label={humanizeKey(String(device.deviceStatus).replace(/_/g, ' '))} />}
                        </Box>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={3}><InfoItem label='MAC address' value={device.macAddress} mono /></Grid>
                            <Grid item xs={12} sm={3}><InfoItem label='Hostname' value={device.hostname} /></Grid>
                            <Grid item xs={12} sm={3}><InfoItem label='Report collected' value={device.hardwareAt ? dayjs(device.hardwareAt).format(DATE_FORMAT) : null} /></Grid>
                            <Grid item xs={12} sm={3}><InfoItem label='Device ID' value={device.deviceId} mono /></Grid>
                        </Grid>
                    </Box>

                    <Tabs value={tab} onChange={(event, value) => setTab(value)} sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}>
                        <Tab value='overview' label='Overview' sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }} />
                        <Tab value='report' label='Full report' sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }} />
                    </Tabs>

                    <Box sx={{ px: 3, py: 2.5, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                        {tab === 'overview' && <OverviewTab device={device} options={options} />}
                        {tab === 'report' && <ReportTab device={device} />}
                    </Box>
                </>}
            </Box>
        </Modal>
    );
}
